import { NgxSpinnerService } from "ngx-spinner";
import Swal from "sweetalert2";
import { swalError } from "./swalCommon";
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { Value } from "src/app/interfaces/value";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { EMAIL_PATTERN, FERIADOS_COLOMBIA } from "./config";
import { DirectionObjRecover } from "src/app/interfaces/direction-structure.interface";

export const buildUrlPaged = (page?: number, limit?: number, nextPages?: number, offset?: number) => {
    const parms = {
        sPage: (page) ? `Page=${page}` : null,
        sLimit: (limit) ? `Limit=${limit}` : null,
        sNextP: (nextPages) ? `NextPages=${nextPages}` : null,
        sOffset: (offset) ? `Offset=${offset}` : null
    };


    const haveParam = (page && limit && nextPages && offset) ? '?' : null;

    return `${(haveParam) ? haveParam : ''}`;
};

const toUserId = (value: any): number => {
    const id = Number(value);
    return Number.isFinite(id) && id > 0 ? id : 0;
};

/** userIds de los participantes de la solicitud: titular, segundo titular y codeudores. */
export const getPetitionParticipantIds = (petition: any): number[] => [
    petition?.userId,
    petition?.secondHolder?.userId,
    ...(Array.isArray(petition?.coDebtors) ? petition.coDebtors : []).map((c: any) => c?.user?.id ?? c?.userId)
].map(toUserId).filter(id => id > 0);

/**
 * Codeudor de la solicitud que corresponde al usuario, o null. Se usa en lugar del rol global
 * "Codeudor": un usuario puede ser codeudor en una solicitud y titular en otra (y un codeudor que ya
 * existía como usuario puede no tener ese rol).
 */
export const findPetitionCoDebtor = (petition: any, userId: any): any => {
    const id = toUserId(userId);
    if (!id || !Array.isArray(petition?.coDebtors)) return null;
    return petition.coDebtors.find((c: any) => toUserId(c?.user?.id ?? c?.userId) === id) || null;
};

/**
 * Determina con qué userId se consultan la capacidad de pago y los documentos de una solicitud.
 * 1. Si el usuario logueado es parte de la solicitud (titular, segundo titular o codeudor),
 *    siempre se usan sus propios datos: antes el codeudor caía al userId del titular y se le
 *    pedían los documentos de la actividad económica del titular.
 * 2. Asesor/analista: el userId pedido (p.ej. query param), luego el elegido en la bandeja, luego
 *    AdjudgedUserId; cada uno solo si pertenece a esta solicitud (un valor viejo de otra solicitud
 *    consultaba los documentos de otra persona). Si no, el titular.
 * Si la solicitud no trae los codeudores con su id (dato incompleto) no se puede validar y se acepta el valor.
 */
export const resolvePetitionUserId = (petition: any, loggedUserId: number, requestedUserId?: any): number => {
    const participants = getPetitionParticipantIds(petition);
    // Solo se valida la pertenencia si todos los codeudores traen su id (user.id o userId).
    const canValidate = Array.isArray(petition?.coDebtors)
        && petition.coDebtors.every((c: any) => toUserId(c?.user?.id ?? c?.userId) > 0);
    const belongs = (id: number) => id > 0 && (participants.includes(id) || !canValidate);

    const logged = toUserId(loggedUserId);
    if (logged > 0 && participants.includes(logged)) return logged;

    const candidates = [requestedUserId, sessionStorage.getItem('selectedUserIdFromBandeja'), sessionStorage.getItem('AdjudgedUserId')];
    for (const candidate of candidates) {
        const id = toUserId(candidate);
        if (belongs(id)) return id;
    }

    return toUserId(petition?.userId) || petition?.userId;
};

export const substracTime = (date: Date, minutes: number) => {
    const fecha = new Date(date);
    const min = fecha.getMinutes();

    fecha.setMinutes(min - minutes);
    return fecha;
};

export const destroyTable = (dtInst: Promise<DataTables.Api>) => (dtInst) ? dtInst
    .then((dtInstance: DataTables.Api) => dtInstance.destroy()) : new Promise((resolve) => resolve(resolve));

export const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
    const base64 = b64Data.split(',');
    const byteCharacters = atob(base64[1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
};

export const getNewYears = () => {
    const currentYear = new Date().getFullYear();
    let years: string[] = [];
    years.push(String(currentYear - 1));
    years.push(String(currentYear));
    years.push(String(currentYear + 1));

    return years;
  }

  export const getUsedYears = () => {
    // Año mínimo fijo definido por negocio para vehículos usados (no es una ventana móvil).
    const minYear = 2012;
    const currentYear = new Date().getFullYear();
    let years: string[] = [];

    for(let year = minYear; year <= currentYear + 1; year++) {
      years.push(String(year));
    }

    return years;
  }

  export const getFileName = (upFile) => upFile.match(/[^\\/]*$/)[0];

  export const getBase64 = (file: File) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => reject(error);
  });

  /**
   * Extrae solo el contenido Base64 puro, removiendo el prefijo data:application/pdf;base64,
   * @param base64String cadena con formato data:application/pdf;base64,<base64_content> o solo <base64_content>
   * @returns cadena Base64 pura sin el prefijo
   */
  export const extractBase64 = (base64String: string): string => {
    if (!base64String) {
      return base64String;
    }
    // Si contiene el prefijo, extraerlo
    if (base64String.includes('base64,')) {
      return base64String.split('base64,')[1];
    }
    // Si no contiene prefijo, retornar como está
    return base64String;
  };

  export const createTabName = (docName: string) => {
    return docName.toLowerCase().trim().normalize("NFD").replace(/\sde\s|\sa\s|\sla\s/g, " ").replace(/[\u0300-\u036f]/g, "").replace(/\s/g, '-');
  }

  export const openPdf = (data: string) => {
    if (data != undefined) {
      const searchBase64 = data.match('data:application/pdf;base64');
      if (searchBase64 == null) {
        generateBlob(data);
      } else {
        data = data.split('base64,')[1];
        generateBlob(data);
      }
    }
  }

  export const generateBlob = (base64Pdf: string) => {
    const byteCharacters = atob(base64Pdf);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: 'application/pdf;base64' });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

/**
 * Muestra mensaje general de error
 * @param error información adicional del error
 * @param spinner servicio spinner
 * @returns
 */
export const handleError = (error: string, spinner: NgxSpinnerService) => spinner.hide().then(() => Swal.fire(swalError('Ha ocurrido un error', error)));

/**
 * Valida un formulario
 * @param formGroup formulario a validar
 */
export const validarForm = (formGroup: FormGroup | FormArray) => {
  Object.keys(formGroup.controls).forEach(field => {
    const control = formGroup.get(field);
    if (control instanceof FormControl) {
      control.markAsTouched({ onlySelf: true });
    } else if (control instanceof FormGroup || control instanceof FormArray) {
      validarForm(control);
    }
  });
}

/**
 * Ordena alfabeticamente una lista de Values por el name (defecto) valueId (numerico)
 * @param arr       Values[]
 * @param orderName por defecto true
 * @returns
 */
export const abcSort = (arr: Value[], orderName = true) => arr?.sort((a, b) => orderName ? a.name.localeCompare(b.name) : parseInt(a.valueId, 0) - parseInt(b.valueId, 0));

/**
 * Filtra los valores de acuerdo a @param type
 * @param type      grupo de Values a recuperar
 * @param data      Values[] de donde filtar
 * @returns
 */
export const search = (type: string, data: any[]) => data?.filter(x => x.type === type);

/**
 * Guarda los campos que se han diligenciado en el formulario
 * @param formGroup formulario del que se tomarán los campos
 * @param formIdentifier nombreFormulario_petitionId para distinguirlo de otros formularios guardados
 */
export const saveForm = (formGroup: FormGroup, formIdentifier = 'formSaved') => {
  localStorage.setItem(formIdentifier, JSON.stringify(formGroup.value));
}

/**
 * Carga los campos guardados en el localStorage para restaurarlos en el formulario
 * @param formGroup formulario al que se le restaurarán los campos
 * @param formIdentifier nombreFormulario_petitionId para distinguirlo de otros formularios guardados
 */
export const loadForm = (formGroup: FormGroup, formIdentifier = 'formSaved') => {
  const formSaved = JSON.parse(localStorage.getItem(formIdentifier));
  if(formSaved){
    try {
      formGroup.patchValue(formSaved);

    } catch (error) {
      console.log(formGroup);


    }
   }
}

/**
 * Elimina los campos guardados en el localStorage para evitar que se acumule información que ya no es necesaria
 * @param formIdentifier nombreFormulario_petitionId para distinguirlo de otros formularios guardados
 */
export const deleteForm = (formIdentifier = 'formSaved') => {
  localStorage.removeItem(formIdentifier);
}

/**
 * Valida que el control no tenga espacios en blanco
 * @param control control al que se le aplicará la validación
 */
export const blankSpaces: ValidatorFn = (control: AbstractControl):  ValidationErrors | null => {
  let result = false;
  if(control && control.value !== null) {
    result = control.value.includes(' ');
  }
  return result === false ? null : { hasSpaces: true }
}

/**
 * Retorna la hora inicial del asesor con el formato de timepicker
 * @param time hora de inicio del asesor, por ejemplo: 8:00 a.m
 */
export const setStartPicker = (time: string) => {
  const aux1 = time.split(':');
  const aux2 = aux1[1].split(' ');
  let hour = {hour: 8, minute: 0, second: 0};
  if(aux2[1] === "a.m") {
    hour = {hour: Number(aux1[0]), minute: Number(aux2[0]), second: 0};
  }
  else if(aux2[1] === "p.m") {
    hour = {hour: Number(aux1[0]) < 12 ? Number(aux1[0]) + 12 : Number(aux1[0]), minute: Number(aux2[0]), second: 0};
  }

  return hour;
}

/**
 * Retorna true si el día es feriado o fin de semana para deshabilitarlo del DatePicker
 * @param date fecha de cada día del DatePicker
 */
export const isHoliday = (date: NgbDateStruct): boolean => {
  const d = new Date(date.year, date.month - 1, date.day);
  const saturday = 6;
  const sunday = 0;
  const exist = FERIADOS_COLOMBIA.find(f => f.day === date.day && f.month === date.month);
  const isHoliday = exist ? true : false;

  return isHoliday || d.getDay() === sunday;
}

export const clearField = (field: AbstractControl) => {
  field.clearValidators();
  field.updateValueAndValidity();
}

export const requiredField = (field: AbstractControl, email = false) => {
  field.setValidators(email ? [Validators.required, Validators.pattern(EMAIL_PATTERN)] : Validators.required);
  field.updateValueAndValidity();
}

export const resetField = (field: AbstractControl) => {
  clearField(field);
  field.reset();
}

export const validateField = (field: AbstractControl) => {
  field.markAsTouched();
  field.updateValueAndValidity();
}

export const stringAddressToObj = (address: string): DirectionObjRecover => {
  if(address != null) {
    const parts = address.split(" ");

    const addressObj: DirectionObjRecover = {
      street: parts[0],
      streetNumber: parts[1],
      houseNumber: parts[2],
      oficeNumber: Number(parts[3]),
      apartment: parts[4] !== "null" ? parts[4] : null,
      apartmentNumber: parts[5] !== "null" ? parts[5] : null,
      neighborhood: parts[6] !== "null" ? parts[6] : null,
      neighborhoodField: parts[7] !== "null" ? parts[7] : null
    }

    return addressObj;
  }
  else return null;
}

export const isNullOrEmpty = (str) => (!str?.length);

/**
 * Retorna los elementos del ng-picker ordenados primero por el resultado más sencillo y exacto. Sirve para otros arreglos
 * @param term término de búsqueda
 * @param item item del arreglo en ese instante
 */
export const startsWithSearch = (term: string, item: string) => {
  const lowTerm = term.toLowerCase();
  const lowItem = item.toLowerCase();
  return lowItem.startsWith(lowTerm);
}
